/**
 * EventList - Grid display of event cards with loading and empty states
 */

import type { TourStop } from '../../api/events';
import { EventCard } from './EventCard';
import { NearbyClassesFallback } from './NearbyClassesFallback';

interface EventListProps {
  events: TourStop[];
  locationQuery?: string;
  isLoading?: boolean;
  error?: string | null;
  /** Nearest stops from a wider fallback search, rendered in the empty state (SDA-257). */
  fallbackStops?: TourStop[];
  /** The user's selected radius, surfaced in the fallback recovery copy. */
  searchRadius?: number;
}

export function EventList({ events, locationQuery, isLoading, error, fallbackStops, searchRadius }: EventListProps) {
  // Loading state
  if (isLoading) {
    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <EventCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-danger-100 text-danger-500 mb-4">
          <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-warm-900 mb-2">
          Unable to Load Events
        </h3>
        <p className="text-warm-600 max-w-md mx-auto">{error}</p>
      </div>
    );
  }

  // Empty state
  if (events.length === 0) {
    if (fallbackStops && fallbackStops.length > 0) {
      return (
        <NearbyClassesFallback
          stops={fallbackStops}
          searchRadius={searchRadius ?? 50}
          locationQuery={locationQuery ?? ''}
        />
      );
    }

    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-warm-100 text-warm-400 mb-4">
          <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-warm-900 mb-2">
          No Events Found
        </h3>
        <p className="text-warm-600 max-w-md mx-auto">
          We couldn't find any Broadway tours in your area right now.
          Try expanding your search radius or check back soon!
        </p>
      </div>
    );
  }

  // Results grid
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {events.map((event) => (
        <EventCard key={event.id} event={event} locationQuery={locationQuery} />
      ))}
    </div>
  );
}

/**
 * Skeleton loader for event cards
 */
function EventCardSkeleton() {
  return (
    <div className="card animate-pulse">
      {/* Image skeleton */}
      <div className="aspect-[3/2] bg-warm-200 rounded-lg mb-4" />

      {/* Content skeleton */}
      <div className="space-y-3">
        <div className="h-6 bg-warm-200 rounded w-3/4" />
        <div className="h-4 bg-warm-100 rounded w-1/2" />
        <div className="h-4 bg-warm-100 rounded w-1/3" />
        <div className="h-4 bg-warm-100 rounded w-2/5" />
        <div className="h-10 bg-warm-200 rounded w-full mt-4" />
      </div>
    </div>
  );
}

export default EventList;
